import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { portfolioImportPostApplyErrorMessage } from '../utils/portfolioImportEvents';
import { uppercaseLabel } from '../styles/typography';
import { frostedPanelBg, popoverElevationShadow } from '../styles/surfaceSx';
import { analyticsToggleGroupSx } from '../styles/analyticsMsiStyles';
import {
    APP_STYLE_COMPONENT_COLORS,
    APP_STYLE_PALETTE,
    MSI_GEOMETRY,
    MSI_TYPE_SCALE,
} from '../styles/msiStyleTokens';
import {
    watchlistInsetPanelSx,
    watchlistPanelSx,
    watchlistPopoverPaperSx,
    watchlistSecondaryControlSx,
} from '../styles/watchlistMsiStyles';
import {
    Alert,
    Box,
    Button,
    Chip,
    Collapse,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Popover,
    Paper,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import * as XLSX from 'xlsx';
import { callBackend } from '../utils/api';
import { fetchLatestPortfolioImportStatus } from '../utils/importStatusClient';
import {
    isPreviewStaleImportApplyError,
    isWatchlistStaleImportApplyError,
    parseCloudRunError,
    portfolioImportReviewVersionKey,
} from './msiDriftReviewHelpers';

import PortfolioUploader from './PortfolioUploader';
import PendingImportCard from './PendingImportCard';
import ImportDiffDetails from './ImportDiffDetails';
import {
    HistoryCategoryChip,
    HistoryStatusIcon,
    HistorySummaryMetrics,
    historyLedgerRowSx,
} from './shared/HistoryLedgerPrimitives';

const tabs = [
    { value: 'manual', label: 'Manual Upload', shortLabel: 'Upload' },
];

const sourceChipConfig = {
    manual_upload: {
        label: 'Manual',
        color: 'secondary',
        tooltip: 'Import added via manual file upload',
    },
    ibkr: {
        label: 'IBKR',
        color: 'info',
        tooltip: 'Import added via IBKR sync',
    },
    ibkr_intraday: {
        label: 'IBKR Intraday',
        color: 'info',
        tooltip: 'Import added from Performance intraday sync',
    },
    restore: {
        label: 'Restored',
        color: 'success',
        tooltip: 'Import added via restoring a previous import',
    },
    watchlist_sync: {
        label: 'Watchlist Sync',
        color: 'warning',
        tooltip: 'Import generated to keep your portfolio aligned with your watchlist',
    },
    watchlist_editor: {
        label: 'Watchlist Editor',
        color: 'primary',
        tooltip: 'Import staged from the Watchlist portfolio editor',
    },
    file_drop: {
        label: 'File Drop',
        color: 'primary',
        tooltip: 'Import added via file-drop',
        sx: {
            borderColor: (theme) => theme.palette.primary.main,
            color: (theme) => theme.palette.primary.main,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
        },
    },
};

const statusIconConfig = {
    applied: {
        label: 'Complete',
        kind: 'success',
        color: 'success.main',
        tooltip: 'Portfolio import completed and was applied',
    },
    superseded: {
        label: 'Superseded',
        kind: 'warning',
        color: 'warning.main',
        tooltip: 'Portfolio import was superseded and was not applied',
    },
    failed: {
        label: 'Failed',
        kind: 'error',
        color: 'error.main',
        tooltip: 'Portfolio import failed and was not applied',
    },
    pending_apply: {
        label: 'Awaiting review',
        kind: 'pending',
        color: 'info.main',
        tooltip: 'Portfolio import is waiting for review',
    },
    running: {
        label: 'Updating',
        kind: 'info',
        color: 'info.main',
        tooltip: 'Portfolio import is updating',
        loading: true,
    },
};

export default function PortfolioIngestPanel({
    selectedFactorModel,
    serverNormalVol,
    serverMaxPosSize,
    aum,
    default_target_vol_pct,
    default_target_vol_dollar,
    default_scaler,
    onImportApplied,
    watchlistIsReady = true,
    variant = 'panel',
    onGuideInteraction,
    showRecentButton = true,
    openRecentRequestId = 0,
    userUid = null,
    reviewImportRun = null,
    onPendingResolved,
}) {
    const isRail = variant === 'rail';
    const isModal = variant === 'modal';
    const isHistory = variant === 'history';
    const isReview = variant === 'review';
    const [activeTab, setActiveTab] = useState('manual');
    const [railAnchorEl, setRailAnchorEl] = useState(null);
    const [imports, setImports] = useState({ latest_pending: null, latest_failed: null, recent_runs: [] });
    const [applyLoading, setApplyLoading] = useState(false);
    const [applyError, setApplyError] = useState('');
    const [watchlistStaleReviewKeys, setWatchlistStaleReviewKeys] = useState(() => new Set());
    const [restoreTarget, setRestoreTarget] = useState(null);
    const [restoreLoading, setRestoreLoading] = useState(false);
    const [restoreError, setRestoreError] = useState('');
    const [expandedRunId, setExpandedRunId] = useState(null);
    const [recentOpen, setRecentOpen] = useState(false);
    const [recentRuns, setRecentRuns] = useState([]);
    const [recentOffset, setRecentOffset] = useState(0);
    const [recentHasMore, setRecentHasMore] = useState(false);
    const [recentLoading, setRecentLoading] = useState(false);
    const [importsLoading, setImportsLoading] = useState(true);
    const recentLimit = 10;
    const [snapshotOpen, setSnapshotOpen] = useState(false);
    const [snapshotRun, setSnapshotRun] = useState(null);
    const [snapshotItems, setSnapshotItems] = useState([]);
    const [snapshotOffset, setSnapshotOffset] = useState(0);
    const [snapshotHasMore, setSnapshotHasMore] = useState(false);
    const [snapshotLoading, setSnapshotLoading] = useState(false);
    const [snapshotError, setSnapshotError] = useState('');
    const snapshotLimit = 100;
    const [snapshotDownloading, setSnapshotDownloading] = useState(false);
    const [failureDetailOpen, setFailureDetailOpen] = useState(false);
    const [failureDetailText, setFailureDetailText] = useState('');
    const [pendingOpen, setPendingOpen] = useState(false);
    const [lastPendingId, setLastPendingId] = useState(null);
    const [refreshedReviewRun, setRefreshedReviewRun] = useState(null);
    const handledRecentRequestRef = useRef(openRecentRequestId);
    const appliedImportRunIdsRef = useRef(new Set());
    const applyOwner = useMemo(() => ({}), [userUid]);
    const applyOwnerRef = useRef(applyOwner);
    applyOwnerRef.current = applyOwner;
    useEffect(() => {
        applyOwnerRef.current = applyOwner;
        setApplyLoading(false);
        setApplyError('');
        setRefreshedReviewRun(null);
        setPendingOpen(false);
        return () => {
            if (applyOwnerRef.current === applyOwner) applyOwnerRef.current = null;
        };
    }, [applyOwner]);

    useEffect(() => {
        setRefreshedReviewRun(null);
    }, [
        reviewImportRun?.id,
        reviewImportRun?.source_metadata_json?.staged_state_hash,
    ]);

    const formatQty = (val) => {
        if (val === null || val === undefined) return '—';
        if (typeof val === 'number') return val;
        const parsed = Number(val);
        return Number.isNaN(parsed) ? String(val) : parsed;
    };

    const refreshImports = useCallback(async (options = {}) => {
        setImportsLoading(true);
        try {
            const resp = await fetchLatestPortfolioImportStatus({
                ...options,
                cacheKey: userUid ?? 'default',
            });
            setImports(resp || {});
            setRecentRuns(resp?.recent_runs || []);
            setRecentOffset((resp?.recent_runs || []).length);
            setRecentHasMore((resp?.recent_runs || []).length >= recentLimit);
        } catch (err) {
            console.error('Failed to fetch import runs:', err);
        } finally {
            setImportsLoading(false);
        }
    }, [userUid]);

    useEffect(() => {
        refreshImports();
    }, [refreshImports]);

    useEffect(() => {
        setWatchlistStaleReviewKeys(new Set());
    }, [userUid]);

    useEffect(() => {
        const handler = () => {
            refreshImports();
        };
        window.addEventListener('imports-updated', handler);
        return () => window.removeEventListener('imports-updated', handler);
    }, [refreshImports]);


    const handleApply = async (run) => {
        const isCurrentOwner = () => applyOwnerRef.current === applyOwner;
        const appliedKey = `${userUid}:${run?.id}`;
        const runId = run?.id == null ? '' : String(run.id);
        const reviewKey = portfolioImportReviewVersionKey(run);
        if (
            !runId
            || !reviewKey
            || applyLoading
            || appliedImportRunIdsRef.current.has(appliedKey)
            || run?.is_watchlist_stale === true
            || watchlistStaleReviewKeys.has(reviewKey)
        ) return;
        setApplyLoading(true);
        setApplyError('');
        try {
            await callBackend(`api/portfolio/imports/${run.id}/apply`, 'POST', {
                expected_staged_state_hash: run?.source_metadata_json?.staged_state_hash ?? null,
            });
            if (!isCurrentOwner()) return;
            appliedImportRunIdsRef.current.add(appliedKey);
            await onImportApplied?.();
            if (!isCurrentOwner()) return;
            await refreshImports({ force: true });
            if (!isCurrentOwner()) return;
            setPendingOpen(false);
            onPendingResolved?.();
        } catch (err) {
            if (!isCurrentOwner()) return;
            console.error('Failed to apply import:', err);
            if (appliedImportRunIdsRef.current.has(appliedKey)) {
                setApplyError(portfolioImportPostApplyErrorMessage(err));
                return;
            }
            const parsedError = parseCloudRunError(err);
            setApplyError(
                parsedError.message
                || 'Failed to apply import. Please try again.',
            );
            if (isWatchlistStaleImportApplyError(parsedError)) {
                setWatchlistStaleReviewKeys((current) => {
                    if (current.has(reviewKey)) return current;
                    const next = new Set(current);
                    next.add(reviewKey);
                    return next;
                });
                try {
                    const refreshedRun = await callBackend(
                        `api/portfolio/imports/${run.id}`,
                        'GET',
                    );
                    if (!isCurrentOwner()) return;
                    setRefreshedReviewRun(refreshedRun || null);
                } catch (_refreshError) {
                    // The reviewed version remains blocked even if refresh fails.
                }
                await refreshImports({ force: true });
            } else if (isPreviewStaleImportApplyError(parsedError)) {
                try {
                    const refreshedRun = await callBackend(
                        `api/portfolio/imports/${run.id}`,
                        'GET',
                    );
                    if (!isCurrentOwner()) return;
                    setRefreshedReviewRun(refreshedRun || null);
                    await refreshImports({ force: true });
                } catch (_refreshError) {
                    // Keep the stale token so another Apply remains fail-closed.
                }
            }
        } finally {
            if (isCurrentOwner()) setApplyLoading(false);
        }
    };

    const handleIgnore = async (run) => {
        if (!run?.id) return;
        setApplyError('');
        try {
            await callBackend(`api/portfolio/imports/${run.id}/ignore`, 'POST');
            await refreshImports({ force: true });
            setPendingOpen(false);
            onPendingResolved?.();
        } catch (err) {
            console.error('Failed to ignore import:', err);
        }
    };

    const handleRestore = async () => {
        if (!restoreTarget?.id) return;
        setRestoreLoading(true);
        setRestoreError('');
        try {
            await callBackend(`api/portfolio/imports/${restoreTarget.id}/restore`, 'POST');
            await refreshImports({ force: true });
            setRestoreTarget(null);
        } catch (err) {
            const parsed = parseCloudRunError(err);
            setRestoreError(parsed.message || 'Failed to prepare the portfolio restore.');
        } finally {
            setRestoreLoading(false);
        }
    };

    const handleLoadMore = async () => {
        if (recentLoading || !recentHasMore) return;
        setRecentLoading(true);
        try {
            const resp = await callBackend(
                `api/portfolio/imports/?offset=${recentOffset}&limit=${recentLimit}`,
                'GET',
            );
            const items = resp?.items || [];
            setRecentRuns((prev) => [...prev, ...items]);
            setRecentOffset((prev) => prev + items.length);
            setRecentHasMore(Boolean(resp?.has_more));
        } catch (err) {
            console.error('Failed to load more imports:', err);
        } finally {
            setRecentLoading(false);
        }
    };

    const loadSnapshot = async (run, offset = 0, replace = true) => {
        if (!run?.id) return;
        setSnapshotLoading(true);
        setSnapshotError('');
        try {
            const resp = await callBackend(
                `api/portfolio/imports/${run.id}/snapshot?offset=${offset}&limit=${snapshotLimit}`,
                'GET',
            );
            const items = resp?.items || [];
            setSnapshotItems((prev) => (replace ? items : [...prev, ...items]));
            setSnapshotOffset(offset + items.length);
            setSnapshotHasMore(Boolean(resp?.has_more));
        } catch (err) {
            setSnapshotError(err?.message || 'Failed to load snapshot.');
        } finally {
            setSnapshotLoading(false);
        }
    };

    const openSnapshot = (run) => {
        setSnapshotRun(run);
        setSnapshotOpen(true);
        setSnapshotItems([]);
        setSnapshotOffset(0);
        setSnapshotHasMore(false);
        loadSnapshot(run, 0, true);
    };

    const downloadSnapshot = async () => {
        if (!snapshotRun?.id) return;
        setSnapshotDownloading(true);
        try {
            const allItems = [];
            let offset = 0;
            let hasMore = true;
            const exportLimit = 500;
            while (hasMore) {
                const resp = await callBackend(
                    `api/portfolio/imports/${snapshotRun.id}/snapshot?offset=${offset}&limit=${exportLimit}`,
                    'GET',
                );
                const items = resp?.items || [];
                allItems.push(...items);
                hasMore = Boolean(resp?.has_more);
                offset += items.length;
                if (items.length === 0) break;
            }
            const exportData = allItems.map((row) => ({
                sedol: row.sedol,
                ticker: row.ticker,
                quantity: row.quantity,
            }));
            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Snapshot');
            const datePart = snapshotRun?.created_at
                ? new Date(snapshotRun.created_at).toISOString().slice(0, 10)
                : 'snapshot';
            XLSX.writeFile(wb, `portfolio_snapshot_${datePart}.xlsx`);
        } catch (err) {
            console.error('Failed to download snapshot:', err);
        } finally {
            setSnapshotDownloading(false);
        }
    };

    const pendingRunValue = (
        isReview && (refreshedReviewRun || reviewImportRun)
    ) || imports?.latest_pending;
    const pendingRun = pendingRunValue
        && watchlistStaleReviewKeys.has(
            portfolioImportReviewVersionKey(pendingRunValue),
        )
        ? { ...pendingRunValue, is_watchlist_stale: true }
        : pendingRunValue;
    const latestApplied = imports?.recent_runs?.find((run) => run.status === 'applied');
    const latestAppliedId = latestApplied?.id;
    const currentPortfolioTimestamp = latestApplied?.applied_at || latestApplied?.updated_at || latestApplied?.created_at;
    const watchlistStatus =
        watchlistIsReady == null ? 'checking' : watchlistIsReady ? 'ready' : 'missing';
    const importsDisabled = watchlistStatus !== 'ready';
    const railPopoverOpen = Boolean(railAnchorEl);
    const watchlistRequiredTooltip =
        watchlistStatus === 'checking'
            ? 'Checking for watchlist…'
            : 'Please upload a watchlist first.';

    useEffect(() => {
        if (isReview) return;
        if (pendingRun?.id && pendingRun.id !== lastPendingId) {
            setApplyError('');
            if (pendingRun?.source !== 'ibkr_intraday') {
                setPendingOpen(true);
            }
            setLastPendingId(pendingRun.id);
        }
    }, [isReview, pendingRun?.id, pendingRun?.source, lastPendingId]);

    useEffect(() => {
        if (!isModal) return;
        if (!openRecentRequestId) return;
        if (handledRecentRequestRef.current === openRecentRequestId) return;
        handledRecentRequestRef.current = openRecentRequestId;
        onGuideInteraction?.();
        setRecentOpen(true);
    }, [isModal, onGuideInteraction, openRecentRequestId]);

    const renderSourceChip = (source) => {
        const config = sourceChipConfig[source] || {
            label: source || 'Unknown',
            color: 'default',
        };
        return (
            <HistoryCategoryChip
                label={config.label}
                color={config.color}
                tooltip={config.tooltip || ''}
                sx={config.sx || {}}
            />
        );
    };

    const renderStatusIcon = (status) => {
        const config = statusIconConfig[status] || {
            label: status || 'Status unavailable',
            kind: 'info',
            color: 'text.secondary',
        };
        return (
            <HistoryStatusIcon
                label={config.label}
                tooltip={config.tooltip || config.label}
                kind={config.kind}
                color={config.color}
                loading={Boolean(config.loading)}
            />
        );
    };

    const renderRestoreAction = (run) => {
        if (run.status === 'pending_apply' || run.status === 'failed') return null;
        if (run.status === 'applied' && run.id === latestAppliedId) return null;
        return (
            <Tooltip title="Apply this portfolio as current">
                <span>
                    <Button
                        variant="outlined"
                        color="success"
                        size="medium"
                        onClick={() => {
                            setRestoreError('');
                            setRestoreTarget(run);
                        }}
                        disabled={run.status === 'running'}
                        startIcon={<CloudUploadOutlinedIcon fontSize="small" />}
                        sx={{ textTransform: 'none', fontWeight: 600, px: 1.5, minHeight: 32 }}
                    >
                        Apply as New
                    </Button>
                </span>
            </Tooltip>
        );
    };

    const renderSnapshotAction = (run) => {
        if (run.status === 'failed') return null;
        return (
            <Tooltip title="View the portfolio as of this date">
            <span>
                <Button
                    variant="outlined"
                    color="info"
                    size="medium"
                    onClick={() => openSnapshot(run)}
                    startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                    sx={{ textTransform: 'none', fontWeight: 600, px: 1.5, minHeight: 32 }}
                >
                    View Snapshot
                </Button>
            </span>
        </Tooltip>
        );
    };

    const renderFailureDetailsAction = (run) => {
        if (run.status !== 'failed' || !run.error_message) return null;
        return (
            <Tooltip title="View failure details">
                <span>
                    <Button
                        variant="outlined"
                        color="error"
                        size="medium"
                        onClick={() => {
                            setFailureDetailText(run.error_message);
                            setFailureDetailOpen(true);
                        }}
                        startIcon={<ErrorOutlineIcon fontSize="small" />}
                        sx={{ textTransform: 'none', fontWeight: 600, px: 1.5, minHeight: 32 }}
                    >
                        View Details
                    </Button>
                </span>
            </Tooltip>
        );
    };
    const renderPendingActions = (run) => {
        if (run.status !== 'pending_apply') return null;
        const canIgnore = run.source !== 'watchlist_sync';
        return (
            <>
                <Tooltip title="Accept this import">
                    <span>
                        <Button
                            variant="contained"
                            color="success"
                            size="medium"
                            onClick={() => {
                                setRecentOpen(false);
                                setPendingOpen(true);
                            }}
                            startIcon={<CheckCircleOutlineIcon fontSize="small" />}
                            sx={{ textTransform: 'none', fontWeight: 600, px: 1.5, minHeight: 32 }}
                        >
                            Accept
                        </Button>
                    </span>
                </Tooltip>
                {canIgnore && (
                    <Tooltip title="Ignore this import">
                        <span>
                            <Button
                                variant="outlined"
                                color="warning"
                                size="medium"
                                onClick={() => handleIgnore(run)}
                                startIcon={<NotInterestedIcon fontSize="small" />}
                                sx={{ textTransform: 'none', fontWeight: 600, px: 1.5, minHeight: 32 }}
                            >
                                Ignore
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </>
        );
    };

    const toggleExpanded = (runId) => {
        setExpandedRunId((prev) => (prev === runId ? null : runId));
    };

    const closeRailPopover = () => {
        setRailAnchorEl(null);
    };

    const handleRailTabButtonClick = (event, tabValue) => {
        if (importsDisabled) return;
        onGuideInteraction?.();
        const nextAnchor = event.currentTarget;
        const sameTab = activeTab === tabValue;
        const sameAnchor = railAnchorEl === nextAnchor;

        setActiveTab(tabValue);
        if (railPopoverOpen && sameTab && sameAnchor) {
            closeRailPopover();
            return;
        }
        setRailAnchorEl(nextAnchor);
    };

    const railSourceButtonSx = (isActive) => ({
        minHeight: 28,
        px: 0,
        pl: 1.05,
        pr: 0.35,
        borderRadius: `${MSI_GEOMETRY.radii.button}px`,
        border: '1px solid',
        borderColor: isActive ? APP_STYLE_COMPONENT_COLORS.focusBorder : APP_STYLE_PALETTE.lineDark,
        backgroundColor: isActive ? APP_STYLE_PALETTE.blue100 : APP_STYLE_PALETTE.surface,
        color: isActive ? APP_STYLE_PALETTE.ink900 : APP_STYLE_PALETTE.ink700,
        fontSize: '0.66rem',
        fontWeight: 700,
        letterSpacing: 0,
        textTransform: 'none',
        whiteSpace: 'nowrap',
        boxShadow: 'none',
        '& .MuiButton-endIcon': {
            ml: 0.6,
            mr: 0,
            pl: 0.5,
            borderLeft: '1px solid',
            borderColor: APP_STYLE_PALETTE.line,
            color: APP_STYLE_PALETTE.ink500,
        },
        '&:hover': {
            borderColor: isActive
                ? APP_STYLE_COMPONENT_COLORS.focusBorder
                : APP_STYLE_COMPONENT_COLORS.secondaryHoverBorder,
            backgroundColor: isActive
                ? APP_STYLE_PALETTE.blue100
                : APP_STYLE_COMPONENT_COLORS.secondaryHover,
            boxShadow: 'none',
        },
    });

    const railPopoverContent = (
        <Box sx={{ minWidth: 0 }}>
            {importsDisabled ? (
                watchlistStatus === 'checking' ? (
                    <Box
                        sx={[{
                            border: '1px solid',
                            borderColor: 'divider',
                            px: 1,
                            py: 0.6,
                            minHeight: 42,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                        }, watchlistInsetPanelSx]}
                    >
                        <CircularProgress size={15} thickness={5} sx={{ flexShrink: 0 }} />
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                lineHeight: 1.35,
                                fontSize: '0.8rem',
                                pl: 1,
                            }}
                        >
                            Checking for watchlist...
                        </Typography>
                    </Box>
                ) : (
                    <Paper
                        variant="outlined"
                        sx={[{
                            p: 1.15,
                            backgroundColor: (theme) => frostedPanelBg(theme, { darkAlpha: 0.32, lightAlpha: 0.52 }),
                        }, watchlistInsetPanelSx]}
                    >
                        <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                            Please upload a watchlist first to enable portfolio imports.
                        </Alert>
                    </Paper>
                )
            ) : (
                <>
                    {activeTab === 'manual' && (
                        <Paper
                            variant="outlined"
                            sx={[{
                                p: 1,
                                backgroundColor: (theme) => frostedPanelBg(theme),
                            }, watchlistInsetPanelSx]}
                        >
                            <PortfolioUploader
                                selectedFactorModel={selectedFactorModel}
                                serverNormalVol={serverNormalVol}
                                serverMaxPosSize={serverMaxPosSize}
                                aum={aum}
                                default_target_vol_pct={default_target_vol_pct}
                                default_target_vol_dollar={default_target_vol_dollar}
                                default_scaler={default_scaler}
                                onUploadSuccess={refreshImports}
                                compact
                            />
                        </Paper>
                    )}
                </>
            )}
        </Box>
    );

    const modalSectionSx = {
        border: '1px solid',
        p: 1,
        ...watchlistPanelSx,
    };

    const modalSectionTitleSx = uppercaseLabel({
        fontSize: MSI_TYPE_SCALE.tableHeader.fontSize,
        letterSpacing: 0,
        color: 'text.secondary',
        mb: 0.75,
    });

    const modalSectionsContent = (
        <Box sx={{ minWidth: 0, display: 'grid', gap: 1 }}>
            {importsDisabled ? (
                watchlistStatus === 'checking' ? (
                    <Box
                        sx={[{
                            border: '1px solid',
                            borderColor: 'divider',
                            px: 1,
                            py: 0.85,
                            minHeight: 46,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                        }, watchlistInsetPanelSx]}
                    >
                        <CircularProgress size={15} thickness={5} sx={{ flexShrink: 0 }} />
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                lineHeight: 1.35,
                                fontSize: '0.8rem',
                                pl: 1,
                            }}
                        >
                            Checking for watchlist...
                        </Typography>
                    </Box>
                ) : (
                    <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                        Please upload a watchlist first to enable portfolio imports.
                    </Alert>
                )
            ) : (
                <>
                    <Paper variant="outlined" sx={modalSectionSx}>
                        <Typography sx={modalSectionTitleSx}>
                            Manual Upload
                        </Typography>
                        <PortfolioUploader
                            selectedFactorModel={selectedFactorModel}
                            serverNormalVol={serverNormalVol}
                            serverMaxPosSize={serverMaxPosSize}
                            aum={aum}
                            default_target_vol_pct={default_target_vol_pct}
                            default_target_vol_dollar={default_target_vol_dollar}
                            default_scaler={default_scaler}
                            onUploadSuccess={refreshImports}
                            compact
                        />
                    </Paper>
                </>
            )}
        </Box>
    );

    const renderRecentImportsContent = () => (
        <>
            {importsLoading && recentRuns.length === 0 && (
                <Box display="flex" alignItems="center" justifyContent="center" minHeight={140}>
                    <CircularProgress size={24} />
                    <Typography ml={1.5} variant="body2">Loading history...</Typography>
                </Box>
            )}
            {!importsLoading && recentRuns.length === 0 && (
                <Typography variant="body2">No portfolio history is available.</Typography>
            )}
            {recentRuns.map((run, index) => (
                <Box key={run.id}>
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.75}
                        flexWrap="wrap"
                        sx={historyLedgerRowSx({
                            current: run.status === 'applied' && run.id === latestAppliedId,
                            last: index === recentRuns.length - 1,
                        })}
                    >
                        {run.status === 'applied' && run.id === latestAppliedId && (
                            <Tooltip title="Current portfolio" arrow>
                                <Box
                                    component="span"
                                    aria-label="Current portfolio"
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        ml: 0.15,
                                        borderRadius: '50%',
                                        bgcolor: 'primary.main',
                                        boxShadow: (theme) => (
                                            `0 0 7px ${alpha(theme.palette.primary.main, 0.65)}`
                                        ),
                                    }}
                                />
                            </Tooltip>
                        )}
                        {renderStatusIcon(run.status)}
                        {renderSourceChip(run.source)}
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.67rem', whiteSpace: 'nowrap' }}
                        >
                            {new Date(run.created_at).toLocaleString()}
                        </Typography>
                        <Box
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="flex-end"
                            gap={0.8}
                            sx={{ ml: 'auto', pl: 0.75 }}
                        >
                            <HistorySummaryMetrics
                                added={Number(run.diff_summary_json?.counts?.added || 0)}
                                removed={Number(run.diff_summary_json?.counts?.removed || 0)}
                                changed={Number(run.diff_summary_json?.counts?.changed || 0)}
                                itemLabel="positions"
                            />
                            <Tooltip title={expandedRunId === run.id ? 'Collapse' : 'Expand'}>
                                <IconButton
                                    size="small"
                                    onClick={() => toggleExpanded(run.id)}
                                    aria-label={`${expandedRunId === run.id ? 'Collapse' : 'Expand'} portfolio import`}
                                >
                                    {expandedRunId === run.id ? (
                                        <ExpandLessIcon fontSize="small" />
                                    ) : (
                                        <ExpandMoreIcon fontSize="small" />
                                    )}
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    <Collapse in={expandedRunId === run.id} timeout="auto" unmountOnExit>
                        <Box sx={{ mt: 1, mb: 0.5 }}>
                            <Paper variant="outlined" sx={[{ p: 1.5 }, watchlistPanelSx]}>
                                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1} mb={1} flexWrap="wrap">
                                    {renderPendingActions(run)}
                                    {renderSnapshotAction(run)}
                                    {renderFailureDetailsAction(run)}
                                    {renderRestoreAction(run)}
                                </Box>
                                <ImportDiffDetails
                                    diffDetails={run.diff_details_json}
                                    diffSummary={run.diff_summary_json}
                                    emptyMessage={
                                        run.status === 'failed'
                                            ? 'Import failed: changes not applied.'
                                            : undefined
                                    }
                                />
                            </Paper>
                        </Box>
                    </Collapse>
                </Box>
            ))}
            {recentHasMore && (
                <Box display="flex" justifyContent="center" mt={1}>
                    <Button size="small" variant="outlined" onClick={handleLoadMore} disabled={recentLoading}>
                        {recentLoading ? 'Loading…' : 'Load More'}
                    </Button>
                </Box>
            )}
        </>
    );

    if (isReview) {
        return (
            <Box sx={{ minHeight: 180 }}>
                {applyError && (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                        {applyError}
                    </Alert>
                )}
                {pendingRun ? (
                    <PendingImportCard
                        importRun={pendingRun}
                        onApply={handleApply}
                        onIgnore={pendingRun?.source === 'watchlist_sync' ? undefined : handleIgnore}
                        applying={applyLoading}
                        showActions={!appliedImportRunIdsRef.current.has(`${userUid}:${pendingRun?.id}`)}
                        currentPortfolioTimestamp={currentPortfolioTimestamp}
                    />
                ) : (
                    <Box
                        sx={{
                            minHeight: 180,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            color: 'text.secondary',
                        }}
                    >
                        <CircularProgress size={18} />
                        <Typography variant="body2">Loading pending import...</Typography>
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Box display="flex" flexDirection="column" gap={0.7} sx={{ height: '100%' }}>
            {((!isModal && !isHistory) || showRecentButton) && (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent={isModal ? 'flex-end' : 'space-between'}
                    gap={0.6}
                    flexWrap="wrap"
                >
                    {!isModal && !isHistory && (
                        <Box display="flex" alignItems="center" gap={0.45} flexWrap="wrap">
                            <Tooltip title={importsDisabled ? watchlistRequiredTooltip : ''} disableHoverListener={!importsDisabled}>
                                <span style={{ display: 'inline-flex' }}>
                                    {isRail ? (
                                        <Box display="flex" alignItems="center" gap={0.45} flexWrap="wrap">
                                            {tabs.map((tab) => {
                                                const isOpen = railPopoverOpen && activeTab === tab.value;
                                                return (
                                                    <Button
                                                        key={tab.value}
                                                        variant="text"
                                                        size="small"
                                                        disabled={importsDisabled}
                                                        onClick={(event) => handleRailTabButtonClick(event, tab.value)}
                                                        endIcon={
                                                            <ExpandMoreIcon
                                                                sx={{
                                                                    fontSize: 16,
                                                                    transform: isOpen ? 'rotate(180deg)' : 'none',
                                                                    transition: 'transform 160ms ease',
                                                                }}
                                                            />
                                                        }
                                                        sx={railSourceButtonSx(activeTab === tab.value)}
                                                    >
                                                        {tab.shortLabel}
                                                    </Button>
                                                );
                                            })}
                                        </Box>
                                    ) : (
                                        <ToggleButtonGroup
                                            value={activeTab}
                                            exclusive
                                            size="small"
                                            onChange={(_, value) => {
                                                if (!value) return;
                                                onGuideInteraction?.();
                                                setActiveTab(value);
                                            }}
                                            sx={[{
                                                borderRadius: `${MSI_GEOMETRY.radii.button}px`,
                                                overflow: 'visible',
                                                p: 0.25,
                                                minHeight: 30,
                                                '& .MuiToggleButton-root': {
                                                    minHeight: 28,
                                                    py: 0.15,
                                                    px: 0.95,
                                                    fontSize: '0.66rem',
                                                    ...uppercaseLabel({ letterSpacing: 0 }),
                                                    borderRadius: `${MSI_GEOMETRY.radii.button}px`,
                                                    border: 0,
                                                    '&:first-of-type': {
                                                        borderTopLeftRadius: `${MSI_GEOMETRY.radii.button}px`,
                                                        borderBottomLeftRadius: `${MSI_GEOMETRY.radii.button}px`,
                                                    },
                                                    '&:last-of-type': {
                                                        borderTopRightRadius: `${MSI_GEOMETRY.radii.button}px`,
                                                        borderBottomRightRadius: `${MSI_GEOMETRY.radii.button}px`,
                                                    },
                                                },
                                            }, analyticsToggleGroupSx]}
                                        >
                                            {tabs.map((tab) => (
                                                <ToggleButton key={tab.value} value={tab.value} disabled={importsDisabled}>
                                                    {tab.label}
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                    )}
                                </span>
                            </Tooltip>
                        </Box>
                    )}

                    {showRecentButton && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={!isModal ? <CloudUploadOutlinedIcon fontSize="small" /> : undefined}
                            onClick={() => {
                                onGuideInteraction?.();
                                setRecentOpen(true);
                            }}
                            disabled={!imports?.recent_runs?.length}
                            sx={[{
                                minHeight: 28,
                                px: 0.95,
                                borderRadius: `${MSI_GEOMETRY.radii.button}px`,
                                fontSize: '0.66rem',
                                fontWeight: 700,
                                letterSpacing: 0,
                                textTransform: isModal ? 'none' : 'uppercase',
                            }, watchlistSecondaryControlSx]}
                        >
                            {isRail ? 'Recent' : 'Recent Imports'}
                        </Button>
                    )}
                </Box>
            )}

            {!isRail && !isModal && !isHistory && (
                <Box sx={{ minHeight: 0, flex: 1 }}>
                    {railPopoverContent}
                </Box>
            )}

            {isModal && (
                <Box sx={{ minHeight: 0, flex: 1, overflowY: 'auto' }}>
                    {modalSectionsContent}
                </Box>
            )}

            {isHistory && (
                <Box sx={{ minHeight: 0, flex: 1 }}>
                    {importsDisabled && (
                        <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.75rem' }}>
                            {watchlistStatus === 'checking'
                                ? 'Checking for watchlist before enabling portfolio uploads…'
                                : 'Upload a watchlist first to enable new portfolio imports. Existing history remains available below.'}
                        </Alert>
                    )}
                    {renderRecentImportsContent()}
                </Box>
            )}

            {isRail && (
                <Popover
                    open={railPopoverOpen}
                    anchorEl={railAnchorEl}
                    onClose={closeRailPopover}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{
                        sx: [{
                            mt: 0.75,
                            width: { xs: 'min(92vw, 360px)', sm: 360 },
                            maxWidth: 'calc(100vw - 32px)',
                            p: 0.8,
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: popoverElevationShadow,
                        }, watchlistPopoverPaperSx],
                    }}
                >
                    {railPopoverContent}
                </Popover>
            )}

            <Dialog open={Boolean(restoreTarget)} onClose={() => setRestoreTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Restore portfolio snapshot?</DialogTitle>
                <DialogContent dividers>
                    {restoreError && (
                        <Alert severity="error" sx={{ mb: 1.5 }}>
                            {restoreError}
                        </Alert>
                    )}
                    <Typography variant="body2">
                        This will create a new import from this historical snapshot. You’ll review the diff and still need
                        to click Apply to update your portfolio.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setRestoreError('');
                            setRestoreTarget(null);
                        }}
                        disabled={restoreLoading}
                    >
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleRestore} disabled={restoreLoading}>
                        {restoreLoading ? 'Staging...' : 'Stage'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={snapshotOpen} onClose={() => setSnapshotOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography variant="subtitle1">Portfolio Snapshot</Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
                            onClick={downloadSnapshot}
                            disabled={snapshotDownloading || snapshotLoading}
                            sx={{ textTransform: 'none' }}
                        >
                            {snapshotDownloading ? 'Downloading…' : 'Download'}
                        </Button>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {snapshotError && (
                        <Alert severity="error" sx={{ mb: 1 }}>
                            {snapshotError}
                        </Alert>
                    )}
                    {!snapshotError && (
                        <>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                {snapshotRun?.created_at
                                    ? `Snapshot from ${new Date(snapshotRun.created_at).toLocaleString()}`
                                    : ''}
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Ticker</TableCell>
                                            <TableCell align="right">Quantity</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {snapshotItems.map((row, idx) => (
                                            <TableRow
                                                key={`snapshot-${row.cognive_id || row.ticker || idx}`}
                                                sx={{
                                                    bgcolor: (theme) => (
                                                        idx % 2 === 1 && !theme.palette.highContrast
                                                            ? 'rgba(0,0,0,0.04)'
                                                            : 'transparent'
                                                    ),
                                                }}
                                            >
                                                <TableCell>{row.ticker || '—'}</TableCell>
                                                <TableCell align="right">{formatQty(row.quantity)}</TableCell>
                                            </TableRow>
                                        ))}
                                        {!snapshotLoading && snapshotItems.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={2} align="center">
                                                    This portfolio snapshot had no positions.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {snapshotHasMore && (
                                <Box display="flex" justifyContent="center" mt={1}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => loadSnapshot(snapshotRun, snapshotOffset, false)}
                                        disabled={snapshotLoading}
                                    >
                                        {snapshotLoading ? 'Loading…' : 'Load More'}
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSnapshotOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={failureDetailOpen} onClose={() => setFailureDetailOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Import Failure Details</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary">
                        {failureDetailText || 'No additional details available.'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFailureDetailOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={recentOpen}
                onClose={() => setRecentOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        alignSelf: 'flex-start',
                        mt: '10vh',
                        height: '70vh',
                        maxHeight: '70vh',
                        width: '60vw',
                        maxWidth: 720,
                    },
                }}
            >
                <DialogTitle>Recent Imports</DialogTitle>
                <DialogContent dividers sx={{ overflowY: 'auto' }}>
                    {renderRecentImportsContent()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRecentOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={pendingOpen} onClose={() => setPendingOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Pending Import</DialogTitle>
                <DialogContent dividers>
                    {applyError && (
                        <Alert severity="error" sx={{ mb: 1.5 }}>
                            {applyError}
                        </Alert>
                    )}
                    {pendingRun ? (
                        <PendingImportCard
                            importRun={pendingRun}
                            onApply={handleApply}
                            onIgnore={pendingRun?.source === 'watchlist_sync' ? undefined : handleIgnore}
                            applying={applyLoading}
                            showActions={!appliedImportRunIdsRef.current.has(`${userUid}:${pendingRun?.id}`)}
                            currentPortfolioTimestamp={currentPortfolioTimestamp}
                        />
                    ) : (
                        <Typography variant="body2">No pending import.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}

PortfolioIngestPanel.propTypes = {
    selectedFactorModel: PropTypes.string,
    serverNormalVol: PropTypes.number,
    serverMaxPosSize: PropTypes.number,
    aum: PropTypes.number,
    default_target_vol_pct: PropTypes.number,
    default_target_vol_dollar: PropTypes.number,
    default_scaler: PropTypes.number,
    onImportApplied: PropTypes.func,
    watchlistIsReady: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([null])]),
    variant: PropTypes.oneOf(['panel', 'rail', 'modal', 'history', 'review']),
    onGuideInteraction: PropTypes.func,
    showRecentButton: PropTypes.bool,
    openRecentRequestId: PropTypes.number,
    userUid: PropTypes.string,
    reviewImportRun: PropTypes.object,
    onPendingResolved: PropTypes.func,
};
